import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Shield, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const { forgotPassword, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await forgotPassword(email);

    if (result.success) {
      setIsSuccess(true);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fsf-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Header openContactModal={() => {}} />

        <div className="bg-gradient-to-br from-fsf-primary via-fsf-secondary to-fsf-marriage flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-white hover:text-fsf-warm transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour à la connexion
              </Link>
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Email envoyé !
              </h2>
              <p className="text-gray-600 mb-6">
                Un email de récupération a été envoyé à <strong>{email}</strong>.
                Vérifiez votre boîte de réception et suivez les instructions pour réinitialiser votre mot de passe.
              </p>
              <div className="space-y-4">
                <Link
                  to="/login"
                  className="w-full inline-flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-fsf-primary to-fsf-secondary hover:from-fsf-secondary hover:to-fsf-primary transition-all duration-300"
                >
                  Retour à la connexion
                </Link>
                <button
                  onClick={() => setIsSuccess(false)}
                  className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  Renvoyer l'email
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header openContactModal={() => {}} />

      <div className="bg-gradient-to-br from-fsf-primary via-fsf-secondary to-fsf-marriage flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-white hover:text-fsf-warm transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour à la connexion
            </Link>
          </div>

          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-white rounded-full flex items-center justify-center shadow-lg">
              <Shield className="h-8 w-8 text-fsf-primary" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-white">
              Mot de passe oublié
            </h2>
            <p className="mt-2 text-sm text-fsf-light">
              Entrez votre email pour recevoir un lien de récupération
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  Saisissez l'adresse email associée à votre compte.
                  Nous vous enverrons un lien pour réinitialiser votre mot de passe.
                </p>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-fsf-primary focus:border-fsf-primary focus:z-10"
                    placeholder="votre.email@exemple.com"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-fsf-primary to-fsf-secondary hover:from-fsf-secondary hover:to-fsf-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fsf-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </div>
                  ) : (
                    'Envoyer le lien de récupération'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>
                Vous n'arrivez pas à récupérer votre compte ?{' '}
                <Link
                  to="/"
                  onClick={() => window.location.href = 'mailto:admin@fsf.com'}
                  className="font-medium text-fsf-primary hover:text-fsf-secondary transition-colors duration-200"
                >
                  Contactez le support
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;